const Autodesk = window.Autodesk;
const ForgePotree = window.ForgePotree;
import store from '@/store';
import { EventBus } from '@/event-bus';
import { CROP_BOX_EVENTS } from '@/utils/cropBoxEvents';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import { removeFromScene, setForgeCropBox, pointerToRaycaster } from '../utils/forgeUtils'
import { getBoxControlArrows, drawBoxLines, getBoxOffset } from '../utils/threejsUtils'
import { ForgeAvaInspect } from '@/scripts/js/forgeModels';
import { metresToViewerLength } from '@/utils/coordinateAuthority';
const THREE = window.THREE;

/**
 * Where a live instance parks its handler set, so the NEXT instance constructed
 * against the same DOM nodes can detach them. Stamped on the `#viewer3d` element
 * rather than held in a module variable because the node is the thing the
 * listeners are actually attached to — a module-level singleton would be wrong
 * the moment two viewers exist.
 */
const HANDLER_KEY = '__avaCropping3DHandlers';

class Cropping3DExtension extends Autodesk.Viewing.Extension {
    constructor(loadedViewer, options) {
        super(loadedViewer, options);
        this.viewer = loadedViewer;
        this.webGLDOM = this.viewer.canvas.parentElement.parentElement.parentElement;
        let _this = this;

        // ALL THREE handlers are kept so unload() can detach all three, and any
        // set left on these same DOM nodes by an earlier instance is detached
        // first. Previously only `click` was stored; `mousedown` and `mousemove`
        // were inline arrows with no reference, so they could never be removed.
        //
        // This is not tidiness. Handlers from a superseded instance keep running
        // the code they closed over, so a fix to a handler does not take effect
        // while a stale copy is still attached — measured live: the current
        // instance's onMouseUp honoured its guard and refused to release the
        // navigation lock, while the attached listener released it anyway,
        // defeating the status bar's view lock on the first click.
        this._detachStaleHandlers(this.webGLDOM, this.viewer.canvas);
        this._handlers = {
            click: (event) => { _this.onMouseUp(event); },
            mousedown: (event) => { _this.onMouseDown(event); },
            mousemove: (event) => { _this.onMouseMove(event); },
        };
        // Kept for callers that reference it by its old name.
        this.clickEvent = this._handlers.click;
        this.webGLDOM.addEventListener("click", this._handlers.click);
        this.viewer.canvas.addEventListener("mousedown", this._handlers.mousedown);
        this.webGLDOM.addEventListener("mousemove", this._handlers.mousemove, false);
        this.webGLDOM[HANDLER_KEY] = { handlers: this._handlers, canvas: this.viewer.canvas };
        this.sceneName = 'potree-scene';
        this.cropBoxColor  = 0x5A002A;
        this.cropBoxOffsetColor  = 0xff0000;
        this.pointsColor = 0x000000;

        this.cropBox = null;
        this.cropBoxOffsetMesh = null;
        this.cropBoxMesh = null;
        this.cropBoxPoints = [];
        this.draggingArrowIndex = -1;
        this.cropBoxOrigin = null;//the point in the middle of the cropBox

        this.draggingPoint = null;
        // Crop dragging borrows Forge's one global navigation lock. Keep the
        // inherited value so releasing a handle cannot silently unlock a view
        // that the surveyor deliberately locked from the status bar.
        this.navigationWasLockedBeforeCropDrag = null;
        this.highlightedTriangles = null;

        this.croppingOn = false;
        this.pointTypes ={
            Front : 'Front',
            Back : 'Back',
            Left : 'Left',
            Right : 'Right',
            Top : 'Top',
            Bottom : 'Bottom',
        }

    }

    load() {
         let _this = this;
         this._cropFlushTimer = null;
         this.viewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, () => {
            if(!_this.croppingOn||!_this.cropBox || !store.getters.getIsCropBoxVisible)
                return;
           _this.setCropBoxControls(_this.cropBox);
           _this.viewer.impl.invalidate(false, false, true);
        });

        return true;
    }
    /**
     * Remove a handler set an earlier instance left on these nodes.
     *
     * Safe to call when there is nothing to remove — `removeEventListener` with
     * an unregistered function is a no-op, and the whole thing is guarded.
     */
    _detachStaleHandlers(webGLDOM, canvas) {
        const prev = webGLDOM && webGLDOM[HANDLER_KEY];
        if (!prev || !prev.handlers) return;
        try {
            webGLDOM.removeEventListener("click", prev.handlers.click);
            webGLDOM.removeEventListener("mousemove", prev.handlers.mousemove, false);
            if (prev.canvas) prev.canvas.removeEventListener("mousedown", prev.handlers.mousedown);
            debugWarn('[Cropping3DExtension] detached handlers left by a previous instance');
        } catch (err) {
            debugError('[Cropping3DExtension] could not detach stale handlers', err);
        }
        delete webGLDOM[HANDLER_KEY];
    }

     unload() {
         clearTimeout(this._cropFlushTimer);
         this._releaseNavigationLockAfterCropDrag();
         this.setCropBox(null);
         if (this._handlers) {
             if (this.webGLDOM) {
                 this.webGLDOM.removeEventListener("click", this._handlers.click);
                 this.webGLDOM.removeEventListener("mousemove", this._handlers.mousemove, false);
                 if (this.webGLDOM[HANDLER_KEY] &&
                     this.webGLDOM[HANDLER_KEY].handlers === this._handlers) {
                     delete this.webGLDOM[HANDLER_KEY];
                 }
             }
             if (this.viewer && this.viewer.canvas) {
                 this.viewer.canvas.removeEventListener("mousedown", this._handlers.mousedown);
             }
             this._handlers = null;
             this.clickEvent = null;
         }
         return true;
     }
    onToolbarCreated() {

    }
    /**
     * Drop every reference to the crop-box drag handles and their highlight.
     *
     * INVARIANT: hit-testable <=> visible.
     *
     * `this.cropBoxPoints` is the ONLY hit-test source — checkAndHighlightControlUnderMouse
     * raycasts the array BY REFERENCE, never the overlay scene — while scene membership is
     * the visibility source. `removeFromScene` (forgeUtils.js:610) drops scene membership
     * but never touches this array, so hiding the chrome without clearing it leaves handles
     * that are invisible and still grabbable.
     *
     * That is not a small window. When a caller builds the controls and hides them in the
     * SAME synchronous tick (the verification pre-flight does exactly this), no render runs
     * in between, so `scene.updateMatrixWorld()` never reaches the handles and their
     * matrixWorld stays the identity while the real placement sits on `mesh.position`.
     * The bundled three.js (LMV's r71 fork) raycasts off matrixWorld verbatim, so the
     * handles collapse onto the world origin with a degenerate transform whose hit volume
     * swallows every ray — measured live as 15/15 viewport probes grabbing a handle. Each
     * hit calls navigation.setIsLocked(true) and paints a blue highlight plane, which reads
     * to the user as "the viewer is dead and there is a blue plane in the way".
     *
     * Also drops `highlightedTriangles`: both callers invalidate(true, true, true) right
     * after, so the removal is actually rendered. Without that the plane leaves the scene
     * graph but stays painted, because onMouseMove's own invalidate sits BELOW its
     * `getIsCropBoxVisible` early-return and can never be reached while hidden.
     *
     * Deliberately does NOT touch `cropBox` (the clipping volume is independent of its
     * chrome), `croppingOn`, or `draggingPoint` — clearing draggingPoint would abort an
     * in-progress drag on a transient hide while the lock stayed held. Both show paths
     * repopulate via setCropBoxControls.
     */
    clearCropBoxControlState() {
        removeFromScene(this.highlightedTriangles, this.viewer, this.sceneName);
        this.highlightedTriangles = null;
        this.cropBoxPoints = [];
        debugLog('[Cropping3DExtension] crop handles cleared — hit-test disabled while hidden');
    }
    updateBoxVisibility(visible) {
        if (!this.croppingOn) return;

        store.commit('setIsCropBoxVisible', visible);

        if (visible) {
            this.setCropBox(this.cropBox);
            this.setCropBoxControls(this.cropBox);
        } else {
            removeFromScene(this.cropBoxOffsetMesh, this.viewer, this.sceneName);
            removeFromScene(this.cropBoxMesh, this.viewer, this.sceneName);
            removeFromScene(this.cropBoxPoints.map(o => o.arrow.arrow),this.viewer,this.sceneName);
            removeFromScene(this.cropBoxPoints.map(o => o.arrow.cylinder),this.viewer,this.sceneName);
            // AFTER the removeFromScene calls above — they still need the populated array.
            this.clearCropBoxControlState();
        }

        this.viewer.impl.invalidate(true, true, true);
    }
    toggleCropbox(show){
        this.croppingOn = show;
        if (!show) {
            removeFromScene(this.cropBoxOffsetMesh, this.viewer, this.sceneName);
            removeFromScene(this.cropBoxMesh, this.viewer, this.sceneName);
            removeFromScene(this.cropBoxPoints.map(o => o.arrow.arrow),this.viewer,this.sceneName);
            removeFromScene(this.cropBoxPoints.map(o => o.arrow.cylinder),this.viewer,this.sceneName);
            // AFTER the removeFromScene calls above — they still need the populated array.
            this.clearCropBoxControlState();
            this.setCropBox(null);
        } else {
            this.setCropBox(this.cropBox);
            this.setCropBoxControls(this.cropBox);
        }

        store.commit('setIsCropBoxVisible', show);

        this.viewer.impl.invalidate(true, true, true);
    }
    setCropBoxControls(bBox) {
        //cleared previous drawn
        removeFromScene(this.cropBoxMesh,this.viewer,this.sceneName);
        removeFromScene(this.cropBoxPoints.map(o => o.arrow.arrow),this.viewer,this.sceneName);
        removeFromScene(this.cropBoxPoints.map(o => o.arrow.cylinder),this.viewer,this.sceneName);

        //draw new
        this.cropBoxMesh = drawBoxLines(bBox, this.cropBoxColor,this.viewer,this.sceneName);
        this.drawOffsetCropBox();

        this.cropBoxPoints = getBoxControlArrows(bBox, this.viewer, this.sceneName, 1);
    }
     setCropBox(box){
         // IMMEDIATE — visual feedback must stay per-frame
         this.cropBox = box ? box.clone() : null;
         setForgeCropBox(box, this.viewer);
         var mainExtension = store.state.forgeService.getMainExtension();
         if (mainExtension._pointclouds) {
             var pointclouds = Array.from(mainExtension._pointclouds.values());
             if (pointclouds.length == 0)
                 return;
             pointclouds.forEach(o => o.setClippingBoxNew(box));
         }

         if (box) {
             ForgePotree.Global.boundingBoxStrict = box;

             // DEBOUNCED — expensive O(n) LRU walk + icon/image updates
             clearTimeout(this._cropFlushTimer);
             this._cropFlushTimer = setTimeout(() => {
                 store.state.forgeService.updateAll3DIcons();
                 if (ForgePotree.Global.lru && mainExtension._pointclouds) {
                     var activePCs = Array.from(mainExtension._pointclouds.values());
                     ForgePotree.Global.lru.flushOutsideBox(box, activePCs);
                 }
                 store.dispatch('updateImagesInCropbox');
             }, 200);
         } else {
             // Null path: clear timer immediately, remove strict box immediately
             clearTimeout(this._cropFlushTimer);
             this._cropFlushTimer = null;
             ForgePotree.Global.boundingBoxStrict = null;
             // Restore all 3D icons when crop box is cleared (matches original behaviour)
             store.state.forgeService.updateAll3DIcons();
         }

         // Set pivot point to crop box center when crop box is set.
         let center = this.cropBox ? this.cropBox.getCenter() : null;
         if (center) {
             this.viewer.utilities.setPivotPoint(center, true, true);
         }
     }
    drawOffsetCropBox() {
        removeFromScene(this.cropBoxOffsetMesh, this.viewer, this.sceneName);
        if (!this.cropBox)
            return;
        let offset = parseFloat(store.getters['getDownloadBoxOffset']) / store.state.unitScale;
        if (offset == 0)
            return;
        let offsetBox = getBoxOffset(this.cropBox, offset)
        this.cropBoxOffsetMesh = drawBoxLines(offsetBox, this.cropBoxOffsetColor, this.viewer, this.sceneName);
    }
    checkAndHighlightControlUnderMouse(raycaster,assign){
        //snap to nearest point up to 20 pixels
        let sortedPts = this.cropBoxPoints.map(o => o);
        //search for arrow
        let clynders = sortedPts.map(o => o.arrow.cylinder);
        let cones = sortedPts.map(o => o.arrow.arrow.cone);
        let intersection = raycaster.intersectObjects(clynders);
        let index = -1;
        if (intersection.length > 0)
            index = clynders.indexOf(intersection[0].object);
        else {
            intersection = raycaster.intersectObjects(cones);
            if(intersection.length>0)
                index = cones.indexOf(intersection[0].object);
        }
        if (index != -1) {
            //highlight arrow
            if (index != -1) {
                this.highlightedTriangles = sortedPts[index].highlight();
                clynders[index].material.color = new THREE.Color(0xFFA500);
                cones[index].material.color = new THREE.Color(0xFFA500);

                if (assign)
                    this.draggingPoint = sortedPts[index];
                return true;
            }
        }
        return false;
    }
    onMouseMove(mouse) {
        //clear highlight triangles
        removeFromScene(this.highlightedTriangles,this.viewer,this.sceneName);
        this.highlightedTriangles = null;
        if (!this.croppingOn || !this.viewer.impl || !store.getters.getIsCropBoxVisible)
            return;
        let raycaster = pointerToRaycaster(this.viewer, mouse);

        //reset arrows colors
        this.cropBoxPoints.forEach(o=>o.arrow.cylinder.material.color = new THREE.Color(o.color));
        this.cropBoxPoints.forEach(o=>o.arrow.arrow.cone.material.color = new THREE.Color(o.color));

        if (this.draggingPoint) {
            let center = this.cropBox.getCenter();
            let min = this.cropBox.min;
            let max = this.cropBox.max;
            let plane = null;
            if (this.draggingPoint.type == 'Top' || this.draggingPoint.type == 'Bottom')
                plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -center.y);
            else
                plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -center.z);
            let intersection = raycaster.ray.intersectPlane(plane);
            if (!intersection)
                return;
            switch (this.draggingPoint.type) {
                case 'Front':
                    if (intersection.y < max.y) min.y = intersection.y;
                    break;
                case 'Back':
                    if (intersection.y > min.y) max.y = intersection.y;
                    break;
                case 'Left':
                    if (intersection.x < max.x) min.x = intersection.x;
                    break;
                case 'Right':
                    if (intersection.x > min.x) max.x = intersection.x;
                    break;
                case 'Bottom':
                    if (intersection.z < max.z) min.z = intersection.z;
                    break;
                case 'Top':
                    if (intersection.z > min.z) max.z = intersection.z;
                    break;
            }

            let newBox = new THREE.Box3(min, max);
            if (newBox.min.x > newBox.max.x || newBox.min.y > newBox.max.y || newBox.min.z > newBox.max.z) {
                newBox = this.cropBox;
                return;
            }
            this.setCropBox(newBox);
            this.highlightedTriangles = this.draggingPoint.highlight();
            this.setCropBoxControls(this.cropBox);
        }
        else
            this.checkAndHighlightControlUnderMouse(raycaster,false);

        this.cropBoxPoints.forEach(o=>o.arrow.cylinder.material.needsUpdate = true);
        this.cropBoxPoints.forEach(o=>o.arrow.arrow.cone.material.needsUpdate = true);
        this.viewer.impl.invalidate(false, false, true);
    }
    onMouseDown(mouse) {
        //clear highlight triangles
        removeFromScene(this.highlightedTriangles,this.viewer,this.sceneName);
        this.highlightedTriangles = null;

        if (mouse.which != 1 || !this.croppingOn)
            return;
        let raycaster = pointerToRaycaster(this.viewer, mouse);
        if(this.checkAndHighlightControlUnderMouse(raycaster,true))
            this._lockNavigationForCropDrag();
    }

    /**
     * Temporarily lock navigation while a crop handle owns the pointer.
     *
     * Forge exposes a single boolean lock, not ownership tokens. Remembering
     * the inherited value is therefore essential: a handle drag that starts
     * while View is LOCKED must finish LOCKED, while an ordinary unlocked drag
     * must return navigation to the unlocked state.
     */
    _lockNavigationForCropDrag() {
        if (this.navigationWasLockedBeforeCropDrag !== null) return;
        this.navigationWasLockedBeforeCropDrag =
            !!this.viewer.navigation.getIsLocked?.();
        this.viewer.navigation.setIsLocked(true);
    }

    _releaseNavigationLockAfterCropDrag() {
        if (this.navigationWasLockedBeforeCropDrag === null) return;
        this.viewer.navigation.setIsLocked(this.navigationWasLockedBeforeCropDrag);
        this.navigationWasLockedBeforeCropDrag = null;
    }

    onMouseUp(mouse) {
        if (mouse.which != 1 || !this.croppingOn)
            return;

        const weHeldIt = !!this.draggingPoint;
        this.draggingPoint = null;
        this.draggingArrowIndex = -1;
        this.isDraggingPlane = false;
        if (weHeldIt) {
            this._releaseNavigationLockAfterCropDrag();
            // Consumers own persistence, so announce the final live bounds
            // before a view switch or component teardown can replace them.
            EventBus.$emit(CROP_BOX_EVENTS.DRAG_END, { viewer: this.viewer });
        }
    }

    /**
     * Opens the cropped measurement dialog with Potree measurement tools
     * This method handles the complete workflow of:
     * 1. Validating crop box exists
     * 2. Finding active point cloud
     * 3. Getting point cloud URL from store
     * 4. Opening the measurement dialog with proper data
     */
    openMeasurementDialog() {
        // 1. Validate crop box exists and is active
        if (!this.cropBox || !this.croppingOn) {
            if (window.$notify) {
                window.$notify.warning('Please create a crop box first');
            } else {
                debugWarn('Cannot open measurement dialog: No crop box active');
            }
            return;
        }

        // 2. Get main extension
        const mainExt = store.state.forgeService.getMainExtension();
        if (!mainExt || !mainExt._pointCloudGroups || !mainExt._pointclouds) {
            if (window.$notify) {
                window.$notify.error('No point clouds loaded');
            } else {
                debugError('Cannot open measurement dialog: No point clouds loaded');
            }
            return;
        }

        const selected360Image = store.state.selectedImage?.name;

        // 3. Find visible point clouds
        const visiblePCs = Array.from(mainExt._pointCloudGroups.entries())
            .filter(([name, group]) => group.visible)
            .map(([name]) => name)
            .sort((a, b) => a === selected360Image ? -1 : b === selected360Image ? 1 : 0);

        if (visiblePCs.length === 0) {
            if (window.$notify) {
                window.$notify.error('No visible point clouds');
            } else {
                debugError('Cannot open measurement dialog: No visible point clouds');
            }
            return;
        }

        // 4. Determine point cloud for analyzer (priority: Project PC → Visible PC)
        debugLog('[🔍 PC SELECTION] Visible point clouds:', visiblePCs);
        let selectedPCForAnalyzer = null;
        let pcSource = null;

        // Priority 1: Try to find parent project's point cloud for consistent analysis
        if (store.state.selectedImage) {
            const selectedImage = store.state.selectedImage;
            const parentProject = store.state.projects?.find(p =>
                p.images.some(img => img.id === selectedImage.id)
            );

            if (parentProject) {
                const projectPC = parentProject.projectPointClouds;
                if (projectPC) {
                    selectedPCForAnalyzer = projectPC;
                    pcSource = 'project';
                    debugLog('[🔍 PC SELECTION] [ANALYZER] Using project PC:', parentProject.name);
                }
            }
        }

        // Priority 2: Fall back to first visible point cloud (image PC)
        if (!selectedPCForAnalyzer) {
            const visiblePCFromStore = store.state.pointClouds.find(pc =>
                visiblePCs.some(name => name === pc.name)
            );
            if (visiblePCFromStore) {
                selectedPCForAnalyzer = visiblePCFromStore;
                pcSource = 'visible';
                debugLog('[🔍 PC SELECTION] [ANALYZER] Using visible PC:', visiblePCFromStore.name);
            }
        }

        if (!selectedPCForAnalyzer) {
            if (window.$notify) {
                window.$notify.error('Could not determine point cloud for analyzer');
            }
            debugError('[🔍 PC SELECTION] No point cloud found for analyzer');
            return;
        }

        const selectedPCName = selectedPCForAnalyzer.name;
        debugLog('[🔍 PC SELECTION] Final selection:', selectedPCName, '(source:', pcSource + ')');

        // 5. Get ForgePotree instance for transformation data
        // Note: If using project PC that's not currently visible, instance may not exist
        const forgePCInstance = mainExt._pointclouds.get(selectedPCName);

        if (!forgePCInstance && pcSource === 'visible') {
            // If we're using visible PC and no instance, that's an error
            if (window.$notify) {
                window.$notify.error('Point cloud instance not found');
            } else {
                debugError(`Cannot open measurement dialog: Point cloud instance not found for ${selectedPCName}`);
            }
            return;
        }

        // If project PC is not loaded (instance doesn't exist), use default transformation
        // The analyzer will load it fresh with correct transformations
        const useDefaultTransform = !forgePCInstance;
        if (useDefaultTransform) {
            debugLog('[🔍 PC SELECTION] Project PC not currently loaded, will use default transformation');
        }

        // 6. Use the pre-selected point cloud from analyzer priority logic
        const storePC = selectedPCForAnalyzer;

        if (!storePC || !storePC.url) {
            if (window.$notify) {
                window.$notify.error('Point cloud URL not found');
            } else {
                debugError(`Cannot open measurement dialog: Point cloud URL not found for ${selectedPCName}`);
            }
            return;
        }

        // 7. Log debug info
        debugLog('[🔍 SELECTED PC INFO]', {
            name: selectedPCName,
            url: storePC.url,
            source: pcSource,
            loaded: !!forgePCInstance,
            forgePCOffset: forgePCInstance?.pcoGeometry?.offset ?
                `(${forgePCInstance.pcoGeometry.offset.x.toFixed(3)}, ${forgePCInstance.pcoGeometry.offset.y.toFixed(3)}, ${forgePCInstance.pcoGeometry.offset.z.toFixed(3)})` : 'undefined',
            forgePCPosition: forgePCInstance?.position ?
                `(${forgePCInstance.position.x.toFixed(3)}, ${forgePCInstance.position.y.toFixed(3)}, ${forgePCInstance.position.z.toFixed(3)})` : 'undefined'
        });

        // 8. Prepare complete transformation data
        const transformationData = {
            visible: true,
            cropBox: this.cropBox.clone(), // Clone to prevent mutations
            pointCloudUrl: storePC.url,
            pointCloudName: selectedPCName,
            pointCloudId: storePC.id,
            // Clone all transformation properties to prevent mutations
            // If forgePCInstance doesn't exist (project PC not loaded), use defaults
            forgePCInstance: forgePCInstance ? {
                position: forgePCInstance.position ? forgePCInstance.position.clone() : new THREE.Vector3(0, 0, 0),
                quaternion: forgePCInstance.quaternion ? forgePCInstance.quaternion.clone() : new THREE.Quaternion(),
                scale: forgePCInstance.scale ? forgePCInstance.scale.clone() : new THREE.Vector3(1, 1, 1),
                // Include pcoGeometry data if available
                pcoGeometry: forgePCInstance.pcoGeometry ? {
                    offset: forgePCInstance.pcoGeometry.offset ? forgePCInstance.pcoGeometry.offset.clone() : null,
                    boundingBox: forgePCInstance.pcoGeometry.boundingBox ? forgePCInstance.pcoGeometry.boundingBox.clone() : null
                } : null
            } : {
                // Default transformation for project PC that's not currently loaded
                position: new THREE.Vector3(0, 0, 0),
                quaternion: new THREE.Quaternion(),
                scale: new THREE.Vector3(1, 1, 1),
                pcoGeometry: null
            },
            // Add global transformation context from store
            unitScale: store.state.unitScale,
            globalOffset: store.state.globalOffset ? store.state.globalOffset.clone() : new THREE.Vector3(0, 0, 0)
        };

        debugLog('[Cropping3DExtension] Transformation data being sent:', {
            unitScale: transformationData.unitScale,
            globalOffset: transformationData.globalOffset,
            pcoGeometry: transformationData.forgePCInstance.pcoGeometry ? 'present' : 'missing'
        });

        // 9. Open dialog via store mutation
        store.commit('SET_CROPPED_MEASUREMENT_DIALOG', transformationData);
    }

    /**
     * Get the current crop box as a serializable object
     * @returns {Object|null} Crop volume with Min/Max vectors or null if no crop box
     */
    getCurrentCropBox() {
        if (!this.cropBox) {
            return null;
        }

        return {
            Min: {
                x: this.cropBox.min.x,
                y: this.cropBox.min.y,
                z: this.cropBox.min.z
            },
            Max: {
                x: this.cropBox.max.x,
                y: this.cropBox.max.y,
                z: this.cropBox.max.z
            }
        };
    }

    /**
     * Create a default 1x1x1 crop box centered on a given point
     * @param {THREE.Vector3} point - Center point for the crop box
     * @returns {Object} Crop volume object with Min/Max coordinates
     */
    createDefaultCropBox(point) {
        if (!point) {
            debugWarn('[Cropping3DExtension] Cannot create default crop box without a point');
            return null;
        }

        // Validate point has valid coordinates
        if (typeof point.x !== 'number' || typeof point.y !== 'number' || typeof point.z !== 'number') {
            debugWarn('[Cropping3DExtension] Invalid point coordinates:', point);
            return null;
        }

        // Check for NaN or Infinity values
        if (!isFinite(point.x) || !isFinite(point.y) || !isFinite(point.z)) {
            debugWarn('[Cropping3DExtension] Point coordinates contain NaN or Infinity:', point);
            return null;
        }

        // 0.5 m in the model's unit (mm model = 500). No silent metre fallback when
        // the unit is unknown: that drew a 1 mm box on millimetre models.
        let halfSize;
        try {
            halfSize = metresToViewerLength(0.5);
        } catch (err) {
            debugWarn('[Cropping3DExtension] Cannot size the default crop box:', err.message);
            return null;
        }
        return {
            Min: {
                x: point.x - halfSize,
                y: point.y - halfSize,
                z: point.z - halfSize
            },
            Max: {
                x: point.x + halfSize,
                y: point.y + halfSize,
                z: point.z + halfSize
            }
        };
    }

    /**
     * Save the current crop box to a tag
     * @param {Object} tag - Tag object to save crop volume to
     * @returns {Object|null} Crop volume object or null if no crop box
     */
    saveCropVolumeForTag(tag) {
        const cropVolume = this.getCurrentCropBox();

        if (!cropVolume) return null;

        // Get erased volumes from eraser extension
        const eraserExt = this.viewer.getExtension('PointCloudEraserExtension');
        const erasedVolumes = eraserExt ? eraserExt.exportState().volumes : [];

        // Combined structure (new format)
        const combinedData = {
            cropBox: cropVolume,
            erasedVolumes: erasedVolumes
        };

        if (tag) {
            // Store as JSON string in tag's cropVolumeJson property
            tag.cropVolumeJson = JSON.stringify(combinedData);
            debugLog(`[Cropping3DExtension] Saved crop volume + ${erasedVolumes.length} erased volumes for tag ${tag.id}`);
        }

        return combinedData;
    }

    /**
     * Load and apply a crop box from a tag's saved crop volume
     * @param {Object} tag - Tag object with cropVolumeJson property
     * @returns {boolean} True if crop volume was loaded successfully
     */
    loadCropVolumeForTag(tag) {
        if (!tag || !tag.cropVolumeJson) {
            debugWarn('[Cropping3DExtension] No crop volume found for tag');
            return false;
        }

        try {
            const data = JSON.parse(tag.cropVolumeJson);

            // Handle both old and new format (backward compatible)
            let cropVolume, erasedVolumes;

            if (data.cropBox) {
                // New format: {cropBox: {...}, erasedVolumes: [...]}
                cropVolume = data.cropBox;
                erasedVolumes = data.erasedVolumes || [];
            } else {
                // Old format: {Min: {...}, Max: {...}}
                cropVolume = data;
                erasedVolumes = [];
            }

            // Validate cropVolume
            if (!cropVolume || !cropVolume.Min || !cropVolume.Max) {
                debugError('[Cropping3DExtension] Invalid crop volume format');
                return false;
            }

            // Create THREE.Box3 from saved crop volume
            const box = new THREE.Box3(
                new THREE.Vector3(cropVolume.Min.x, cropVolume.Min.y, cropVolume.Min.z),
                new THREE.Vector3(cropVolume.Max.x, cropVolume.Max.y, cropVolume.Max.z)
            );

            // Apply the crop box
            this.setCropBox(box);
            this.toggleCropbox(true);
            this.setCropBoxControls(box);

            debugLog(`[Cropping3DExtension] Loaded crop volume for tag ${tag.id}`);

            // Load erased volumes if present
            if (erasedVolumes.length > 0) {
                const eraserExt = this.viewer.getExtension('PointCloudEraserExtension');
                if (eraserExt) {
                    eraserExt.importState({ volumes: erasedVolumes });
                }
            }

            this.viewer.impl.invalidate(true, true, true);

            return true;
        } catch (error) {
            debugError('[Cropping3DExtension] Error loading crop volume:', error);
            return false;
        }
    }

    /**
     * Clear the current crop box
     */
    clearCropBox() {
        this.toggleCropbox(false);
        this.setCropBox(null);
        this.viewer.impl.invalidate(true, true, true);
    }

}
Autodesk.Viewing.theExtensionManager.registerExtension('Cropping3DExtension', Cropping3DExtension);
